import type { BlockError } from '@domain/schemas/block-error';
import { ResourceState } from '@domain/schemas/contract.schema';
import type { EvalBudgetStatus } from '@queries/evalRuns';
import { blockErrorMessages } from '@root/helpers/constants';

/** Why a guidance scan cannot run right now, for the intro panel's alert and the button tooltips. */
export interface GuidanceBlock {
  tone: 'danger' | 'warning';
  title: string;
  body: string;
}

/** Guidance is for a contract still being written: Draft and Review only. */
export const GUIDANCE_STATUSES: ResourceState[] = [ResourceState.DRAFT, ResourceState.REVIEW];

const STATUS_LABEL: Partial<Record<ResourceState, string>> = {
  FIRM_ORDER: 'Firm Order',
  FINAL: 'Final',
  ARCHIVED: 'Archived',
  PUBLISHED: 'Published',
  PRE_DRAFT: 'Pre-draft',
};

/** "in 2 hours" / "in 1 hour" / "in under an hour", from the budget's reset stamp. */
const describeReset = (resetsAt: string, now: Date): string => {
  const minutes = (new Date(resetsAt).valueOf() - now.valueOf()) / 60_000;
  if (!Number.isFinite(minutes) || minutes < 60) return 'in under an hour';
  const hours = Math.max(1, Math.round(minutes / 60));
  return hours === 1 ? 'in 1 hour' : `in ${hours} hours`;
};

interface ReadinessInput {
  status?: ResourceState | string;
  /** The schema store's verdict on the form data (mandatory fields, formats). */
  isFormValid: boolean;
  /** Per-block validation errors across the contract (`getBlockValidationErrors`). */
  blockErrors: BlockError[];
  /** Today's token budget; `undefined` (loading or failed) never blocks. */
  budget?: EvalBudgetStatus;
  now?: Date;
}

/**
 * The first reason a guidance scan is off, or `undefined` when it can run.
 * Same bar as finalising the contract (`validateSubmissionForFinalisation`,
 * which the start route enforces too): the whole form valid and no block
 * validation errors, on a contract still in Draft or Review — guidance on a
 * partial or finished contract is noise. The daily token budget comes last:
 * it is the only one that clears on its own.
 */
export const describeGuidanceBlock = ({
  status,
  isFormValid,
  blockErrors,
  budget,
  now = new Date(),
}: ReadinessInput): GuidanceBlock | undefined => {
  if (status && !GUIDANCE_STATUSES.includes(status as ResourceState)) {
    const label = STATUS_LABEL[status as ResourceState] ?? status;
    return {
      tone: 'danger',
      title: 'Guidance is no longer available',
      body: `This contract is in ${label} and can no longer be edited. Guidance scans run while a contract is in Draft or Review.`,
    };
  }
  if (!isFormValid || blockErrors.length > 0) {
    // The design's wording covers the form and blank blocks; anything else
    // (unresolved comments, an unselected variation) gets its finalise message.
    const [firstError] = blockErrors;
    const isIncompleteness =
      !isFormValid || blockErrors.every((error) => error === 'block-empty' || error === 'datapoint-empty');
    return {
      tone: 'danger',
      title: 'Before you can run guidance',
      body: isIncompleteness
        ? 'Complete all mandatory form fields and fill or remove any blank blocks. Guidance needs the full contract to give useful suggestions.'
        : (blockErrorMessages[firstError] ?? firstError),
    };
  }
  if (budget?.exhausted) {
    return {
      tone: 'warning',
      title: 'Temporary limit reached',
      body: `You've reached your usage limit of guidance scans. Your limit will reset ${describeReset(budget.resets_at, now)}.`,
    };
  }
  return undefined;
};
